import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Plus, Calendar as CalendarIcon, RefreshCw, ChevronLeft, ChevronDown, ChevronRight,
  Clock, Video, ArrowLeft, Settings, X, Mic, Zap, Sparkles, Bot, Check,
  MoreHorizontal, Command, AlertCircle, LayoutGrid, CalendarDays, Radio,
  Activity, List, MapPin, Users, User, Link as LinkIcon, ExternalLink,
  ToggleLeft, ToggleRight, Play, Square, AlignLeft
} from 'lucide-react';
import { THEME } from '../theme';
import { useBots } from '../hooks/useBots';
import { useMeetings } from '../hooks/useMeetings';
import { useToast } from '../context/ToastContext';
import EnableBots from '../components/EnableBots';
import BotManager from '../components/BotManager';
import ActiveMeetings from '../components/ActiveMeetings';
import WaitingListModal from '../components/WaitingListModal';
import VoiceBotButton from '../components/VoiceBotButton';
import VoiceBotModal from '../components/VoiceBotModal';

// --- THEME CONSTANTS ---
const STYLES = {
  panelDark: "bg-primary border-r border-white/5",
  panelLight: "bg-white border-r border-slate-200",
  glassHeader: "backdrop-blur-md sticky top-0 z-20 border-b",
  glassHeaderDark: "bg-primary/80 border-white/5",
  glassHeaderLight: "bg-white/80 border-slate-200",
  scrollBar: `
    .custom-scrollbar::-webkit-scrollbar { width: 4px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(156, 163, 175, 0.3); border-radius: 10px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: rgba(156, 163, 175, 0.5); }
  `
};

// --- HELPER: TOGGLE SWITCH ---
const Toggle = ({ label, checked, onChange, darkMode }) => (
  <div className="flex items-center justify-between py-2 cursor-pointer group" onClick={() => onChange(!checked)}>
    <span className={`text-xs font-light transition-colors ${darkMode ? 'text-zinc-400 group-hover:text-zinc-200' : 'text-slate-500 group-hover:text-slate-700'}`}>{label}</span>
    <div className={`w-9 h-5 rounded-full relative transition-colors duration-200 ${checked ? 'bg-secondary' : (darkMode ? 'bg-zinc-700' : 'bg-slate-300')}`}>
      <div className={`absolute top-1 left-1 w-3 h-3 rounded-full bg-white transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </div>
  </div>
);

// --- SUB-COMPONENT: CALENDAR GRID ---
const CalendarGrid = ({ calendarMonth, eventsByDate, selectedDate, setSelectedDate, darkMode }) => {
  const year = calendarMonth.getFullYear();
  const mon = calendarMonth.getMonth();
  const firstDay = new Date(year, mon, 1).getDay();
  const daysInMonth = new Date(year, mon + 1, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getMonth() === mon && today.getFullYear() === year;
  const cells = [];

  for (let i = 0; i < 35; i++) {
    const dayNum = i - firstDay + 1;
    const valid = dayNum > 0 && dayNum <= daysInMonth;
    const d = valid ? new Date(year, mon, dayNum) : null;
    const k = d ? d.toDateString() : `e-${i}`;
    const dayEvents = d ? eventsByDate[k] : null;
    const hasEvents = dayEvents && dayEvents.length > 0;
    const isSel = d && selectedDate === k;
    const isToday = isCurrentMonth && dayNum === today.getDate();

    cells.push(
      <div key={k} onClick={() => d && setSelectedDate(k)}
        className={`
             relative w-full aspect-square rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all duration-150 group
             ${!d ? 'opacity-0 pointer-events-none' : ''}
             ${isSel
            ? 'bg-secondary text-white font-medium'
            : (darkMode ? 'text-zinc-400 hover:bg-white/5 hover:text-zinc-100' : 'text-slate-600 hover:bg-slate-100')}
             ${isToday && !isSel ? (darkMode ? 'text-secondary bg-secondary/10' : 'text-secondary bg-secondary/5') : ''}
           `}
      >
        <span className="text-[11px]">{dayNum}</span>
        {hasEvents && (
          <div className="flex gap-0.5 mt-1">
            {dayEvents.slice(0, 3).map((evt, idx) => (
              <div key={idx} className={`w-1 h-1 rounded-full ${isSel ? 'bg-white/80' : (darkMode ? 'bg-secondary' : 'bg-secondary')}`} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-7 gap-1 p-2">
      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
        <div key={i} className={`text-center text-[10px] font-medium tracking-widest mb-1 ${darkMode ? 'text-zinc-600' : 'text-slate-400'}`}>{d}</div>
      ))}
      {cells}
    </div>
  );
};

// --- MAIN DASHBOARD ---
const Dashboard = ({
  darkMode, user, bots: initialBots, calendarEvents, fetchCalendarEvents,
  calendarLoading, calendarError, isSidebarCollapsed
}) => {
  // --- STATE ---
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showMobileDetails, setShowMobileDetails] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toDateString());
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar');
  const scheduleSlug = user?.username?.trim();
  const scheduleUrl = scheduleSlug ? `/schedule/${scheduleSlug}` : null;

  const [engagementMap, setEngagementMap] = useState({});
  const [activeMeetings, setActiveMeetings] = useState([]);
  const [joiningMeetings, setJoiningMeetings] = useState([]); // Track meetings currently joining
  const { addToast } = useToast();

  // Stage Form State
  const [stageBotId, setStageBotId] = useState('');
  const [stageRecord, setStageRecord] = useState(true);
  const [stageTranscript, setStageTranscript] = useState(true);
  const [stageLink, setStageLink] = useState('');

  // Duration State (New)
  const [instantDuration, setInstantDuration] = useState(10); // Default 10 mins

  // Resize State
  const [colSizes, setColSizes] = useState([20, 30, 50]);
  const isResizing = useRef(null);

  // --- NEW: MOBILE NAVIGATION STATE ---
  const [mobileTab, setMobileTab] = useState('calendar'); // 'agents' | 'calendar' | 'stage'
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // --- WELCOME POPUP STATE ---
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [showVoiceBotModal, setShowVoiceBotModal] = useState(false);
  const [voiceBotConfig, setVoiceBotConfig] = useState({ enableSpeak: true, enableTranscript: true, enableRecording: true });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if this is first time user using backend flag
  useEffect(() => {
    if (user && user.hasSeenWelcome === false) {
      // Show welcome popup after a short delay for first-time users
      const timer = setTimeout(() => setShowWelcomePopup(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Mark welcome as seen when popup is closed
  const handleWelcomeClose = async () => {
    setShowWelcomePopup(false);
    setShowWaitlistModal(false);
    
    // Call backend to mark welcome as seen
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${(process.env.REACT_APP_API_URL || "").replace("http://", "https://")}/auth/welcome-seen`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Failed to mark welcome as seen:', error);
    }
  };

  // --- HOOKS ---
  const { bots, loading: botsLoading, error: botsError, botServiceEnabled, fetchBots, enableBotService, createBot } = useBots();
  const { loading: meetingLoading, error: meetingError, assignBot, engageBot, disengageBot, startBot, stopBot, instantMeeting, fetchActiveMeetings, stopInstantMeeting, checkBulkEngagement } = useMeetings();

  // --- EFFECTS ---
  useEffect(() => { fetchBots(); }, [fetchBots]);

  useEffect(() => {
    if (bots && bots.length > 0 && !stageBotId) {
      setStageBotId(bots[0]._id || bots[0].id);
    }
  }, [bots]);

  useEffect(() => {
    if (selectedEvent) {
      const engagement = engagementMap[selectedEvent.id];
      if (engagement && engagement.bot_id) {
        setStageBotId(engagement.bot_id);
      }
      setStageRecord(true);
      setStageTranscript(true);
      // Auto-switch to stage tab on mobile when event selected
      if (isMobile) setMobileTab('stage');
    } else {
      setStageLink('');
    }
  }, [selectedEvent, engagementMap, isMobile]);

  useEffect(() => {
    const loadActiveMeetings = async () => {
      const meetings = await fetchActiveMeetings();
      setActiveMeetings(meetings);
    };
    loadActiveMeetings();
    const interval = setInterval(loadActiveMeetings, 30000);
    return () => clearInterval(interval);
  }, [fetchActiveMeetings]);

  useEffect(() => {
    const checkEngagements = async () => {
      if (!calendarEvents?.length) return;
      const eventIds = calendarEvents.map(ev => ev.id).filter(Boolean);
      if (eventIds.length) {
        const map = await checkBulkEngagement(eventIds);
        setEngagementMap(map);
      }
    };
    checkEngagements();
  }, [calendarEvents, checkBulkEngagement]);

  // --- HANDLERS ---
  const handleStageAction = async () => {
    if (selectedEvent) {
      const engagement = engagementMap[selectedEvent.id];
      const start = new Date(selectedEvent.startDateTime).getTime();
      const end = new Date(selectedEvent.endDateTime).getTime();
      const durationSeconds = (end - start) / 1000;
      const finalDuration = Math.max(60, durationSeconds);

      if (engagement?.engaged) {
        if (engagement.status === 'running') {
          await handleStop(selectedEvent.id);
        } else {
          await handleDisengage(selectedEvent.id);
        }
      } else {
        const payload = {
          bot_id: stageBotId,
          meetlink: selectedEvent.meetingLink,
          meeting_title: selectedEvent.title,
          start_time: selectedEvent.startDateTime,
          end_time: selectedEvent.endDateTime,
          auto_join: true,
          bot_config: {
            enable_recording: stageRecord,
            enable_transcript: stageTranscript
          },
          min_record_time_seconds: finalDuration,
          calendar_event_id: selectedEvent.id
        };
        await handleAssign(selectedEvent.id, payload);
        await handleEngage(selectedEvent.id);
      }
    } else {
      if (!stageLink) return addToast("Please enter a meeting link", "warning");
      const payload = {
        meetlink: stageLink,
        bot_id: stageBotId,
        bot_config: {
          enable_recording: stageRecord,
          enable_transcript: stageTranscript
        },
        min_record_time_seconds: instantDuration * 60,
        meeting_title: "Instant Meeting"
      };
      await handleInstantJoin(payload);
      setStageLink('');
    }
  };

  const handleAssign = async (meetingId, data) => {
    try {
      await assignBot(meetingId, data);
      setEngagementMap(prev => ({ ...prev, [data.calendar_event_id]: { ...prev[data.calendar_event_id], bot_id: data.bot_id } }));
      addToast("Bot assigned successfully", "success");
    } catch (e) { addToast(e.message, "error"); }
  };
  const handleEngage = async (meetingId) => {
    try {
      const res = await engageBot(meetingId);
      setEngagementMap(prev => ({ ...prev, [selectedEvent.id]: { ...prev[selectedEvent.id], engaged: true, meeting_id: res.meeting_id, status: 'scheduled' } }));
      addToast("Bot engaged for meeting", "success");
    } catch (e) { addToast(e.message, "error"); }
  };
  const handleDisengage = async (meetingId) => {
    try {
      await disengageBot(meetingId);
      setEngagementMap(prev => ({ ...prev, [selectedEvent.id]: { ...prev[selectedEvent.id], engaged: false, status: 'idle' } }));
      addToast("Bot disengaged", "info");
    } catch (e) { addToast(e.message, "error"); }
  };
  const handleStop = async (meetingId) => {
    try {
      await stopBot(meetingId);
      setEngagementMap(prev => ({ ...prev, [selectedEvent.id]: { ...prev[selectedEvent.id], status: 'stopped', engaged: false } }));
      addToast("Bot stopped", "info");
    } catch (e) { addToast(e.message, "error"); }
  };
  const handleInstantJoin = async (data) => {
    // Create a temporary ID for the joining state
    const tempId = Date.now().toString();
    const joiningMeeting = {
      id: tempId,
      meeting_title: data.meeting_title || "Instant Meeting",
      status: "joining",
      bot_id: data.bot_id,
      created_at: new Date().toISOString()
    };

    // Add to joining list
    setJoiningMeetings(prev => [...prev, joiningMeeting]);

    try {
      await instantMeeting(data);
      addToast('Bot is joining the meeting...', 'info');

      // We don't remove it immediately; the activeMeetings poll will eventually pick up the real meeting
      // But we should remove it after a delay or when we see the new meeting appear
      setTimeout(() => {
        setJoiningMeetings(prev => prev.filter(m => m.id !== tempId));
      }, 15000); // Clear after 15s (fallback)

    } catch (e) {
      setJoiningMeetings(prev => prev.filter(m => m.id !== tempId));
      addToast(e.message, "error");
    }
  };
  const handleStopInstantMeeting = async (meetingId) => {
    try { await stopInstantMeeting(meetingId); setActiveMeetings(prev => prev.filter(m => m.meeting_id !== meetingId)); addToast("Meeting ended", "info"); }
    catch (e) { addToast(e.message, "error"); }
  };

  // Handler for launching voice bot from the VoiceBotModal
  const handleVoiceBotLaunch = async (data) => {
    try {
      await handleInstantJoin(data);
      setShowVoiceBotModal(false);
      addToast('Voice bot is joining the meeting...', 'success');
    } catch (e) {
      addToast(e.message, 'error');
    }
  };

  const dateKeyFromEvent = (ev) => (ev.start?.dateTime || ev.start?.date ? new Date(ev.start?.dateTime || ev.start?.date).toDateString() : '');
  const eventsByDate = calendarEvents.reduce((acc, ev) => {
    const k = dateKeyFromEvent(ev);
    if (!k) return acc;
    if (!acc[k]) acc[k] = [];
    acc[k].push(ev);
    return acc;
  }, {});

  const handleMonthChange = (newDate) => {
    setCalendarMonth(newDate);
    fetchCalendarEvents(newDate);
  };

  const handleOpenSchedulePage = () => {
    if (!scheduleUrl) {
      addToast('Set a username in Settings to enable scheduling links', 'warning');
      return;
    }
    window.open(scheduleUrl, '_blank', 'noopener,noreferrer');
  };

  const extractVideoLink = (ev) => {
    if (!ev) return '';
    if (ev.hangoutLink) return ev.hangoutLink;
    const textToScan = `${ev.location || ''} ${ev.description || ''}`;
    const urlRegex = /https?:\/\/(?:[a-zA-Z0-9-]+\.)?(?:zoom\.us|google\.com|microsoft\.com|webex\.com|chime\.aws)\/[^\s"']+/g;
    const matches = textToScan.match(urlRegex);
    if (matches) {
      const validLink = matches.find(url => !url.includes('google.com/calendar/event'));
      if (validLink) return validLink;
    }
    return '';
  };

  const handleEventClick = (ev) => {
    if (!ev) return;
    const start = ev.start?.dateTime || ev.start?.date;
    const end = ev.end?.dateTime || ev.end?.date;
    const time = ev.time || (start ? new Date(start).toLocaleString() : 'TBA');
    let duration = '';
    if (start && end) {
      const mins = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
      duration = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
    }
    const isPast = end ? (new Date(end).getTime() < Date.now()) : false;
    const meetingLink = extractVideoLink(ev);

    setSelectedEvent({
      id: ev.id, title: ev.summary || 'No Title', time, duration,
      date: start ? new Date(start).toDateString() : '',
      type: isPast ? 'past' : 'upcoming', bot: 'Calendar',
      platform: ev.location || 'Google Meet',
      description: ev.description || '', attendees: ev.attendees || [],
      organizer: ev.organizer || null, location: ev.location || '',
      startDateTime: start, endDateTime: end,
      meetingLink: meetingLink,
      htmlLink: ev.htmlLink || ev.hangoutLink,
    });
    setShowMobileDetails(true);
    if (isMobile) setMobileTab('stage'); // Auto switch tab
  };

  // Resizing Logic
  const startResize = (index) => (e) => {
    e.preventDefault(); isResizing.current = index;
    document.addEventListener('mousemove', handleMouseMove); document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
  };
  const handleMouseMove = useCallback((e) => {
    if (isResizing.current === null) return;
    const containerWidth = window.innerWidth - (isSidebarCollapsed ? 80 : 320);
    const deltaPercent = (e.movementX / containerWidth) * 100;
    setColSizes(prev => {
      const newSizes = [...prev];
      const MIN = 20; const MAX = 50;
      if (isResizing.current === 0) {
        const pb = newSizes[0] + deltaPercent; const pe = newSizes[1] - deltaPercent;
        if (pb >= MIN && pb <= MAX && pe >= MIN) { newSizes[0] = pb; newSizes[1] = pe; }
      } else {
        const pe = newSizes[1] + deltaPercent; const ps = newSizes[2] - deltaPercent;
        if (pe >= MIN && ps >= MIN) { newSizes[1] = pe; newSizes[2] = ps; }
      }
      return newSizes;
    });
  }, [isSidebarCollapsed]);
  const handleMouseUp = () => {
    isResizing.current = null;
    document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'default';
  };

  // --- RENDERERS ---

  const renderBotsColumn = () => (
    <div
      className={`h-full flex-col ${darkMode ? STYLES.panelDark : STYLES.panelLight} ${mobileTab === 'agents' ? 'flex w-full' : 'hidden'} md:flex`}
      style={{ width: isMobile ? '100%' : `${colSizes[0]}%` }}
    >
      <div className={`h-14 px-4 flex items-center justify-between ${darkMode ? STYLES.glassHeaderDark : STYLES.glassHeaderLight} ${STYLES.glassHeader}`}>
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-secondary/10 text-secondary"><Bot size={16} strokeWidth={2.5} /></div>
          <h2 className={`text-sm font-medium uppercase tracking-wider ${darkMode ? 'text-zinc-100' : 'text-slate-900'}`}>Agents</h2>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
        {botsLoading ? <div className="flex justify-center p-8"><RefreshCw className="animate-spin text-secondary" /></div>
          : !botServiceEnabled ? <EnableBots onEnable={enableBotService} loading={botsLoading} error={botsError} darkMode={darkMode} />
            : <BotManager bots={bots} onCreateBot={createBot} loading={botsLoading} darkMode={darkMode} />}
      </div>
    </div>
  );

  const renderCalendarColumn = () => (
    <div
      className={`h-full flex-col ${darkMode ? STYLES.panelDark : STYLES.panelLight} ${mobileTab === 'calendar' ? 'flex w-full' : 'hidden'} md:flex`}
      style={{ width: isMobile ? '100%' : `${colSizes[1]}%` }}
    >
      <div className={`h-14 px-4 flex items-center justify-between shrink-0 ${darkMode ? STYLES.glassHeaderDark : STYLES.glassHeaderLight} ${STYLES.glassHeader}`}>
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-secondary/10 text-secondary"><CalendarDays size={16} strokeWidth={2.5} /></div>
          <h2 className={`text-sm font-medium uppercase tracking-wider ${darkMode ? 'text-zinc-100' : 'text-slate-900'}`}>Schedule</h2>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setViewMode(viewMode === 'calendar' ? 'monthPicker' : 'calendar')} className={`p-1.5 rounded-md ${darkMode ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-slate-100 text-slate-400'}`}>
            {viewMode === 'calendar' ? <LayoutGrid size={16} /> : <CalendarIcon size={16} />}
          </button>
          <button onClick={() => fetchCalendarEvents(calendarMonth)} className={`p-1.5 rounded-md ${darkMode ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-slate-100 text-slate-400'}`}>
            <RefreshCw size={16} className={calendarLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      <div className={`shrink-0 border-b ${darkMode ? 'border-white/5 bg-primary/50' : 'border-slate-100 bg-slate-50/50'}`}>
        <div className="flex items-center justify-between px-2 py-2 gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <button onClick={() => handleMonthChange(new Date(calendarMonth.setMonth(calendarMonth.getMonth() - 1)))} className={`p-1 rounded-md ${darkMode ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-slate-200 text-slate-500'}`}><ChevronLeft size={16} /></button>
            <span className={`text-xs font-medium ${darkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{calendarMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
            <button onClick={() => handleMonthChange(new Date(calendarMonth.setMonth(calendarMonth.getMonth() + 1)))} className={`p-1 rounded-md ${darkMode ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-slate-200 text-slate-500'}`}><ChevronRight size={16} /></button>
          </div>
          <button
            type="button"
            onClick={handleOpenSchedulePage}
            disabled={!scheduleUrl}
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border transition ${darkMode ? 'text-secondary border-secondary/40 hover:bg-secondary/10' : 'text-secondary border-secondary/60 hover:bg-secondary/10 bg-white'} ${scheduleUrl ? '' : 'opacity-40 cursor-not-allowed'}`}
            title={scheduleUrl ? 'Open your public scheduling link' : 'Add a username to enable scheduling'}
          >
            <LinkIcon size={12} />
            <span>Schedule</span>
          </button>
        </div>
        {viewMode === 'calendar' ? (
          <CalendarGrid calendarMonth={calendarMonth} eventsByDate={eventsByDate} selectedDate={selectedDate} setSelectedDate={setSelectedDate} darkMode={darkMode} />
        ) : (
          <div className="grid grid-cols-3 gap-2 p-4 h-[280px] overflow-y-auto custom-scrollbar">
            {Array.from({ length: 12 }).map((_, i) => (
              <button key={i} onClick={() => { handleMonthChange(new Date(calendarMonth.getFullYear(), i, 1)); setViewMode('calendar'); }}
                className={`p-2 text-xs font-light rounded-lg ${i === calendarMonth.getMonth() ? 'bg-secondary text-white' : (darkMode ? 'bg-white/5 hover:bg-white/10 text-zinc-300' : 'bg-white border hover:bg-slate-50 text-slate-600')}`}>
                {new Date(0, i).toLocaleString('default', { month: 'short' })}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar bg-opacity-50">
        <div className={`sticky top-0 z-10 px-4 py-2 text-[10px] font-medium uppercase tracking-widest border-b backdrop-blur-sm ${darkMode ? 'bg-primary/90 border-white/5 text-zinc-500' : 'bg-white/90 border-slate-100 text-slate-400'}`}>
          {new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
        </div>
        <div className="p-3 space-y-2">
          {(!eventsByDate[selectedDate] || eventsByDate[selectedDate].length === 0) ? (
            <div className="py-10 text-center opacity-40"><List size={24} className="mx-auto mb-2" /><p className="text-xs">No events</p></div>
          ) : (
            eventsByDate[selectedDate].map((event, idx) => {
              const isSelected = selectedEvent?.id === event.id;
              const engagement = engagementMap[event.id];
              return (
                <div key={idx} onClick={() => handleEventClick(event)}
                  className={`group relative p-3 rounded-lg border transition-all cursor-pointer ${isSelected ? (darkMode ? 'bg-secondary/10 border-secondary/50' : 'bg-secondary/5 border-secondary/20') : (darkMode ? 'bg-white/5 border-transparent hover:bg-white/10' : 'bg-white border-slate-200 hover:border-secondary/30')}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className={`text-sm font-light truncate mb-1 ${darkMode ? 'text-zinc-200' : 'text-slate-800'}`}>{event.summary || 'Untitled'}</h4>
                      <div className={`flex items-center gap-2 text-[10px] font-mono ${darkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        <Clock size={10} /><span>{new Date(event.start?.dateTime || event.start?.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    {engagement?.engaged && (<div className={`w-2 h-2 rounded-full ${engagement.status === 'running' ? 'bg-emerald-500 animate-pulse' : 'bg-secondary'}`} />)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  const renderStageColumn = () => (
    <div
      className={`h-full flex-col bg-transparent ${mobileTab === 'stage' ? 'flex w-full' : 'hidden'} md:flex`}
      style={{ width: isMobile ? '100%' : (showMobileDetails ? `${colSizes[2]}%` : `${colSizes[2]}%`) }}
    >
      <div className="lg:hidden p-4 flex items-center gap-2 border-b border-white/5">
        {selectedEvent ? <button onClick={() => { setSelectedEvent(null); setMobileTab('calendar'); }}><ArrowLeft size={20} /></button> : null}
        <span>{selectedEvent ? 'Event Details' : 'Stage'}</span>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
        <div className="max-w-4xl mx-auto h-full flex flex-col">
          {selectedEvent ? (
            // --- EVENT DETAILS MODE ---
            <div className="flex flex-col h-full animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 pr-4">
                  <h2 className={`text-2xl font-light leading-tight mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>{selectedEvent.title}</h2>
                  <div className={`flex flex-wrap items-center gap-4 text-sm font-light ${darkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                    <div className="flex items-center gap-1.5"><CalendarIcon size={14} /> {selectedEvent.date}</div>
                    <div className="flex items-center gap-1.5"><Clock size={14} /> {selectedEvent.time}</div>
                  </div>
                </div>
                <button onClick={() => { setSelectedEvent(null); if (isMobile) setMobileTab('calendar'); }} className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-slate-100 text-slate-500'}`}>
                  <X size={20} />
                </button>
              </div>

              {/* Metadata Card */}
              <div className={`p-4 rounded-2xl border mb-6 space-y-4 ${darkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-100'}`}>
                {selectedEvent.meetingLink && (
                  <div className="flex items-center gap-3 group">
                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-secondary/20 text-secondary' : 'bg-secondary/10 text-secondary'}`}>
                      <Video size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className={`text-[10px] font-medium uppercase tracking-wider mb-0.5 block ${darkMode ? 'text-zinc-500' : 'text-slate-400'}`}>Meeting Link</label>
                      <a href={selectedEvent.meetingLink} target="_blank" rel="noreferrer" className={`text-sm font-light truncate block hover:underline ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {selectedEvent.meetingLink}
                      </a>
                    </div>
                    <a href={selectedEvent.meetingLink} target="_blank" rel="noreferrer" className={`p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity ${darkMode ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-white text-slate-500'}`}>
                      <ExternalLink size={16} />
                    </a>
                  </div>
                )}

                {selectedEvent.location && (
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-pink-500/20 text-pink-400' : 'bg-pink-100 text-pink-600'}`}>
                      <MapPin size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className={`text-[10px] font-medium uppercase tracking-wider mb-0.5 block ${darkMode ? 'text-zinc-500' : 'text-slate-400'}`}>Location</label>
                      <p className={`text-sm font-light truncate ${darkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{selectedEvent.location}</p>
                    </div>
                  </div>
                )}

                {selectedEvent.description && (
                  <div className="flex gap-3 items-start">
                    <div className={`p-2 rounded-lg shrink-0 mt-1 ${darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
                      <AlignLeft size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className={`text-[10px] font-medium uppercase tracking-wider mb-1 block ${darkMode ? 'text-zinc-500' : 'text-slate-400'}`}>Description</label>
                      <div className={`text-sm font-light leading-relaxed max-h-32 overflow-y-auto custom-scrollbar ${darkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                        {selectedEvent.description.replace(/<[^>]*>?/gm, '')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Agent Control Panel */}
              <div className={`p-5 rounded-2xl border mb-6 ${darkMode ? 'bg-primary/50 border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-secondary" />
                    <span className={`text-sm font-medium uppercase tracking-wider ${darkMode ? 'text-zinc-300' : 'text-slate-700'}`}>Agent Settings</span>
                  </div>
                  {engagementMap[selectedEvent.id]?.engaged && (
                    <span className="text-[10px] font-bold bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full uppercase tracking-wide">Active</span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-xs font-light mb-2 ${darkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Select Agent</label>
                    <div className={`relative flex items-center rounded-xl border transition-all focus-within:ring-2 focus-within:ring-secondary ${darkMode ? 'bg-black/20 border-white/10 hover:border-white/20' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}>
                      <div className={`pl-3 ${darkMode ? 'text-secondary/70' : 'text-secondary'}`}><Bot size={16} /></div>
                      <select
                        value={stageBotId}
                        onChange={(e) => setStageBotId(e.target.value)}
                        className={`w-full appearance-none pl-3 pr-10 py-2.5 rounded-xl text-sm bg-transparent border-none focus:ring-0 ${darkMode ? 'text-white' : 'text-slate-900'}`}
                      >
                        {bots.map(bot => <option key={bot._id || bot.id} value={bot._id || bot.id} className="text-black">{bot.name}</option>)}
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-xs font-light mb-1 ${darkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Capabilities</label>
                    <Toggle label="Record Audio" checked={stageRecord} onChange={setStageRecord} darkMode={darkMode} />
                    <Toggle label="Transcribe" checked={stageTranscript} onChange={setStageTranscript} darkMode={darkMode} />
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-dashed border-white/10">
                  {engagementMap[selectedEvent.id]?.engaged ? (
                    <button onClick={handleStageAction} className="w-full py-2.5 rounded-xl font-medium text-sm bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors border border-red-500/20">
                      Disengage Agent
                    </button>
                  ) : (
                    <button onClick={handleStageAction} className="w-full py-2.5 rounded-xl font-medium text-sm bg-secondary text-white hover:bg-secondary/90 transition-all">
                      Engage Agent
                    </button>
                  )}
                </div>
              </div>

              {/* Guests/Details */}
              <div className="space-y-4">
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-100'}`}>
                  <h4 className={`text-xs font-medium uppercase tracking-wider mb-3 ${darkMode ? 'text-zinc-500' : 'text-slate-400'}`}>Attendees</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedEvent.attendees.length > 0 ? selectedEvent.attendees.map((att, i) => (
                      <div key={i} className={`flex items-center gap-2 px-2 py-1 rounded-md text-xs border ${darkMode ? 'border-white/10 bg-black/20 text-zinc-300' : 'border-slate-200 bg-white text-slate-600'}`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${att.responseStatus === 'accepted' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        {att.email}
                      </div>
                    )) : <p className={`text-xs ${darkMode ? 'text-zinc-500' : 'text-slate-400'}`}>No attendees</p>}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // --- INSTANT MEETING HOME ---
            <div className="flex flex-col gap-8 animate-in fade-in duration-700 pt-8">
              <div>
                <h2 className={`text-3xl font-light mb-2 tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Hello, <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-blue-400">Chief</span>
                </h2>
                <p className={`text-sm font-light ${darkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Your command center is ready.</p>
              </div>

              {/* Voice Bot Button - Prominent CTA */}
              <VoiceBotButton
                user={user}
                darkMode={darkMode}
                onVoiceBotClick={() => setShowVoiceBotModal(true)}
                onWaitlistClick={() => setShowWaitlistModal(true)}
              />

              <div className={`relative overflow-hidden rounded-2xl border p-6 transition-all ${darkMode ? 'bg-primary/40 border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/10 blur-[80px] rounded-full pointer-events-none" />

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 rounded-lg bg-secondary/10 text-secondary"><Zap size={20} strokeWidth={2} /></div>
                    <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>Instant Meeting</h3>
                  </div>

                  <div className="space-y-6">
                    <input
                      type="text"
                      placeholder="Paste Google Meet Link..."
                      value={stageLink}
                      onChange={(e) => setStageLink(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-secondary ${darkMode ? 'border-white/10 text-white placeholder-zinc-600' : 'border-slate-200 text-slate-900 placeholder-slate-400'}`}
                    />

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className={`relative flex items-center rounded-xl border transition-all focus-within:ring-2 focus-within:ring-secondary ${darkMode ? 'bg-black/20 border-white/10 hover:border-white/20' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}>
                          <div className={`pl-3 ${darkMode ? 'text-secondary/70' : 'text-secondary'}`}><Bot size={16} /></div>
                          <select
                            value={stageBotId}
                            onChange={(e) => setStageBotId(e.target.value)}
                            className={`w-full appearance-none pl-3 pr-10 py-3 rounded-xl text-sm bg-transparent border-none focus:ring-0 ${darkMode ? 'text-white' : 'text-slate-800'}`}
                          >
                            {bots.map(bot => <option key={bot._id || bot.id} value={bot._id || bot.id} className="text-black">{bot.name}</option>)}
                          </select>
                          <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" />
                        </div>

                        <button onClick={handleStageAction} className="w-full py-3 rounded-xl font-medium text-sm bg-secondary text-white hover:bg-secondary/90 transition-all">
                          Join Now
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div className="flex gap-4">
                          <Toggle label="Record" checked={stageRecord} onChange={setStageRecord} darkMode={darkMode} />
                          <Toggle label="Transcribe" checked={stageTranscript} onChange={setStageTranscript} darkMode={darkMode} />
                        </div>

                        {stageRecord && (
                          <div className={`p-3 rounded-xl border ${darkMode ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                            <div className="flex justify-between items-center mb-2">
                              <label className={`text-xs font-light ${darkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                                Stop Recording After
                              </label>
                              <span className={`text-xs font-bold ${darkMode ? 'text-secondary' : 'text-secondary'}`}>
                                {instantDuration} min
                              </span>
                            </div>
                            <input
                              type="range"
                              min="1"
                              max="30"
                              step="1"
                              value={instantDuration}
                              onChange={(e) => setInstantDuration(Number(e.target.value))}
                              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-secondary"
                            />
                            <div className="flex justify-between text-[10px] opacity-40 mt-1">
                              <span>1m</span>
                              <span>30m</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-h-[200px]">
                <div className="flex items-center gap-2 mb-4 px-1">
                  <Activity size={14} className="text-emerald-500" />
                  <h3 className={`text-xs font-medium uppercase tracking-widest ${darkMode ? 'text-zinc-500' : 'text-slate-400'}`}>Live Activity</h3>
                </div>
                <ActiveMeetings
                  meetings={activeMeetings}
                  joiningMeetings={joiningMeetings}
                  onStop={handleStopInstantMeeting}
                  loading={meetingLoading}
                  darkMode={darkMode}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // --- MAIN RENDER ---
  return (
    <div className={`relative w-full h-full overflow-hidden flex flex-col md:flex-row ${darkMode ? 'bg-primary text-zinc-100' : 'bg-slate-50 text-slate-900'}`}>
      {/* Waitlist Modal - Used for both first-time welcome and manual trigger */}
      <WaitingListModal
        isOpen={showWelcomePopup || showWaitlistModal}
        onClose={handleWelcomeClose}
        darkMode={darkMode}
      />

      {/* Voice Bot Modal - For access=true users to configure and launch voice bot */}
      <VoiceBotModal
        isOpen={showVoiceBotModal}
        onClose={() => setShowVoiceBotModal(false)}
        darkMode={darkMode}
        bots={bots}
        onLaunch={handleVoiceBotLaunch}
        loading={meetingLoading}
      />
      <style>{STYLES.scrollBar}</style>

      {/* Main Desktop Layout (3 Columns side by side) - On mobile, these are toggled via classes */}
      <div className="flex-1 flex overflow-hidden relative">
        <div className={`absolute top-0 left-0 w-full h-64 opacity-20 pointer-events-none ${darkMode ? 'bg-gradient-to-b from-secondary/30 to-transparent' : 'bg-gradient-to-b from-secondary/20 to-transparent'}`} />

        {renderBotsColumn()}
        <div className="hidden md:flex w-px relative z-30 cursor-col-resize hover:bg-secondary transition-colors group items-center justify-center" onMouseDown={startResize(0)}><div className="w-4 h-full absolute opacity-0 hover:opacity-100" /></div>

        {renderCalendarColumn()}
        <div className="hidden md:flex w-px relative z-30 cursor-col-resize hover:bg-secondary transition-colors group items-center justify-center" onMouseDown={startResize(1)}><div className="w-4 h-full absolute opacity-0 hover:opacity-100" /></div>

        {renderStageColumn()}
      </div>

      {/* MOBILE BOTTOM NAVIGATION */}
      <div className={`md:hidden shrink-0 h-16 border-t flex items-center justify-around z-50 ${darkMode ? 'bg-primary border-white/10' : 'bg-white border-slate-200'}`}>
        <button onClick={() => setMobileTab('agents')} className={`flex flex-col items-center gap-1 ${mobileTab === 'agents' ? 'text-secondary' : 'text-gray-500'}`}>
          <Bot size={20} />
          <span className="text-[10px] font-medium">Agents</span>
        </button>
        <button onClick={() => setMobileTab('calendar')} className={`flex flex-col items-center gap-1 ${mobileTab === 'calendar' ? 'text-secondary' : 'text-gray-500'}`}>
          <CalendarIcon size={20} />
          <span className="text-[10px] font-medium">Schedule</span>
        </button>
        <button onClick={() => setMobileTab('stage')} className={`flex flex-col items-center gap-1 ${mobileTab === 'stage' ? 'text-secondary' : 'text-gray-500'}`}>
          <Zap size={20} />
          <span className="text-[10px] font-medium">Stage</span>
        </button>
      </div>
    </div>
  );
};

export default Dashboard;